import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { createHashRouter, Navigate, RouteObject } from 'react-router-dom';

import store from './store';

import Spinner from './components/elements/spinner';
import ComponentsTestScreen from './screens/tests/components';

import { rapidstorApi } from './api/rapidstor';
import ApiRoot from './screens/api-root';
import BookUnit from './screens/book-unit';
import BookUnitMap from './screens/book-unit-map';
import BookingConfirmed from './screens/booking-confirmed';
import CustomiseBooking from './screens/customise-booking';
import Error from './screens/error';
import Index from './screens/index';
import MoveIn from './screens/move-in';
import MoveInConfirmed from './screens/move-in-confirmed';
import PaymentRedirect from './screens/payment-redirect';
import ReservationWithFee from './screens/reservation-with-fee';
import Root from './screens/root';
import TestRoot from './screens/tests/root';
import UnitListing from './screens/unit-listing';
import Logger from './utils/logging';

const handlePageChange = () => {
    const rsContainer = document.getElementById('rapidstor-v2-frontend');
    const path = window.location.hash.search(
        /(booking|reservation-with-fee|move-in|tests|)/
    );
    if (rsContainer) {
        rsContainer.style.display = 'none';
        if (path !== -1) {
            rsContainer.style.minHeight = '100vh';
            rsContainer.style.height = '';
        } else {
            rsContainer.style.height = '100vh';
            rsContainer.style.minHeight = '';
        }
    }
};

const locationLoader = async () => {
    if (!window.rapidstor_settings.app_config || !window.rapidstor_settings.rapidstor_options) {
        Logger.error(`App config not found: ${window.location}`);
        throw new Response('Error', {
            status: 500,
            statusText: 'App config not found',
        });
    }

    const rsContainer = document.getElementById('rapidstor-v2-frontend');
    handlePageChange();
    const spinner = document.getElementById('spinner');
    if (!spinner) {
        rsContainer?.insertAdjacentHTML(
            'beforebegin',
            renderToStaticMarkup(<Spinner loader size='100' useDataAttribute />)
        );
    }

    const promise = store.dispatch(
        rapidstorApi.endpoints.getLocationByCode.initiate({
            locationCode: window.rapidstor_settings.rapidstor_options.locationCode,
        })
    );

    try {
        const result = await promise.unwrap();
        if (rsContainer && spinner) {
            spinner.style.display = 'none';
            rsContainer.style.display = 'revert';
        }

        if (!window.rapidstor_settings.rapidstor_options.token || !window.rapidstor_settings.rapidstor_options.locationCode) {
            Logger.warn(`[404] Token or LocationCode missing`);
            throw new Response('', {
                status: 404,
                statusText: 'Not Found',
            });
        }

        window.rapidstor_settings.rapidstor_options.locationToken = result?.token;
        window.rapidstor_settings.rapidstor_options.location = result;

        if (!result) {
            Logger.warn(`[404] Token or LocationCode missing`, undefined, [
                {
                    name: 'locationCode',
                    value: window.rapidstor_settings.app_config.journey.locationCode,
                },
                {
                    name: 'corpCode',
                    value: window.rapidstor_settings.app_config.journey.corpCode,
                }
            ]);
            throw new Response('', {
                status: 404,
                statusText: 'The locationCode supplied was not found or not enabled.',
            });
        }

        if (!result.rapidstorEnabled) {
            Logger.warn(`[404] RapidStor not enabled`, result, []);
            throw new Response('', {
                status: 404,
                statusText: 'The locationCode supplied was not found or not enabled.',
            });
        }

        return result;
    } finally {
        promise.unsubscribe();
    }
};

const prodScreens = [
    {
        path: '',
        element: <Index />,
        loader: locationLoader,
        errorElement: <Error />,
    },
    {
        path: 'rapidstor',
        element: <Navigate to={'/'} replace />,
    },
    {
        path: '_rapidstor',
        element: <Navigate to={'/'} replace />,
    },
    {
        path: 'unit-listing',
        element: <UnitListing />,
        loader: locationLoader,
        errorElement: <Error />,
    },
    {
        path: 'customise-booking',
        element: <CustomiseBooking />,
        loader: locationLoader,
        errorElement: <Error />,
    },
    {
        path: 'booking-confirmed/:token',
        element: <BookingConfirmed />,
        loader: locationLoader,
        errorElement: <Error />,
    },
    {
        path: 'reservation-with-fee/:token',
        element: <ReservationWithFee />,
        loader: locationLoader,
        errorElement: <Error />,
    },
    {
        path: 'reservation-with-fee/:token/return',
        element: <ReservationWithFee />,
        loader: locationLoader,
        errorElement: <Error />,
    },
    {
        path: 'move-in/:token',
        element: <MoveIn />,
        loader: locationLoader,
        errorElement: <Error />,
    },
    {
        path: 'move-in-confirmed/:token',
        element: <MoveInConfirmed />,
        loader: locationLoader,
        errorElement: <Error />,
    }
];

function buildJourneyRoutes(): RouteObject[] {
    if (!window.rapidstor_settings.app_config) {
        Logger.error(`App config not found: ${window.location}`);
        return [
            {
                path: '',
                element: <Error />,
            },
        ];
    }

    if (!window.rapidstor_settings.app_config.pages.storageType || window.rapidstor_settings.app_config.journey.persona) {
        const indexScreen = prodScreens.find((screen) => screen.path === '');
        if (indexScreen) {
            prodScreens.splice(prodScreens.indexOf(indexScreen), 1);
        }

        const unitListingScreen = prodScreens.find(
            (screen) => screen.path === 'unit-listing'
        );
        if (unitListingScreen) {
            unitListingScreen.path = '';
        }
    }

    if(process.env.NODE_ENV !== 'production') {
        prodScreens.push({
            path: '/tests',
            element: <TestRoot />,
            loader: locationLoader,
            errorElement: <Error />,
        }, {
            path: '/tests/components',
            element: <ComponentsTestScreen />,
            loader: locationLoader,
            errorElement: <Error />,
        });
    }

    if (window.rapidstor_settings.app_config.journey.dev) {
        if (window.rapidstor_settings.app_config.pages.bookUnit === 'standard') {
            prodScreens.push({
                path: 'book-unit',
                element: <BookUnit />,
                loader: locationLoader,
                errorElement: <Error />,
            }, {
                path: 'book-unit-map',
                element: <BookUnitMap />,
                loader: locationLoader,
                errorElement: <Error />,
            });
        } else {
            prodScreens.push({
                path: 'book-unit',
                element: <BookUnitMap />,
                loader: locationLoader,
                errorElement: <Error />,
            });
        }
    } else {
        prodScreens.push({
            path: 'book-unit',
            element: window.rapidstor_settings.app_config.pages.bookUnit === 'standard' ? <BookUnit /> : <BookUnitMap />,
            loader: locationLoader,
            errorElement: <Error />,
        });
    }

    return prodScreens;
}

export default createHashRouter(
    [
        {
            path: '/',
            element: <Root />,
            loader: locationLoader,
            errorElement: <Error />,
            children: buildJourneyRoutes(),
        },
        {
            path: '/api',
            element: <ApiRoot />,
            loader: locationLoader,
            children: [
                {
                    path: 'payment-redirect/:token/:type',
                    element: <PaymentRedirect />,
                    loader: locationLoader,
                    errorElement: <Error />,
                }
            ]
        },
    ],
    {
        basename: '/',
    }
)
